/**
 * MFA (TOTP) hooks.
 *
 * Covers both halves of the backend contract:
 *  - Challenge-authenticated endpoints (`/auth/mfa/*`), used from the
 *    `/login/mfa` page where the caller has no session yet — only the
 *    short-lived `mfa_challenge` HttpOnly cookie set by `/auth/email/signin`
 *    or an OAuth redirect.
 *  - Account endpoints (`/me/mfa/*`), used from Settings by an already
 *    signed-in user managing their own factor.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../config';
import { toApiError } from '../lib/api-error';

export type MfaEnrollStartResponse = {
  ok: true;
  secret: string;
  otpauthUri: string;
  qrSvgDataUri: string;
};

export type MfaEnrollConfirmResponse = {
  ok: true;
  recoveryCodes: string[];
};

async function postJson<T>(path: string, body: unknown, fallback: string): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await toApiError(res, fallback);
  return res.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Challenge-authenticated (/auth/mfa/*) — no session yet.
// ---------------------------------------------------------------------------

export function useMfaChallengeEnrollStart() {
  return useMutation({
    mutationFn: () =>
      postJson<MfaEnrollStartResponse>('/auth/mfa/enroll/start', {}, 'Failed to start MFA setup'),
  });
}

export function useMfaChallengeEnrollConfirm() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (code: string) =>
      postJson<MfaEnrollConfirmResponse>(
        '/auth/mfa/enroll/confirm',
        { code },
        'Failed to confirm MFA code'
      ),
    onSuccess: () => {
      // Confirming grants the real session cookies.
      queryClient.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

export function useMfaChallengeVerify() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { code: string } | { recoveryCode: string }) =>
      postJson<{ ok: true }>('/auth/mfa/verify', params, 'Failed to verify MFA code'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Account (/me/mfa/*) — normal signed-in session.
// ---------------------------------------------------------------------------

export function useAccountMfaEnrollStart() {
  return useMutation({
    mutationFn: () =>
      postJson<MfaEnrollStartResponse>('/me/mfa/enroll/start', {}, 'Failed to start MFA setup'),
  });
}

export function useAccountMfaEnrollConfirm() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (code: string) =>
      postJson<MfaEnrollConfirmResponse>(
        '/me/mfa/enroll/confirm',
        { code },
        'Failed to confirm MFA code'
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

export function useAccountMfaRegenerateRecoveryCodes() {
  return useMutation({
    mutationFn: () =>
      postJson<MfaEnrollConfirmResponse>(
        '/me/mfa/recovery-codes',
        {},
        'Failed to regenerate recovery codes'
      ),
  });
}

export function useAccountMfaRemove() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/me/mfa`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to remove authenticator app');
      return res.json() as Promise<{ ok: true }>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['me'] });
    },
  });
}
